<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';
import { fetchLog, addNote, updateNote, deleteNote, onDescriptionLogged } from '../setup_log.js';
import { useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';
import { useDeferredCompute } from '../use_deferred_compute.js';
import { formatTimeAgo, useNowTicker } from '../use_relative_time.js';

const { activeSetupId } = useSetups();
const { pushToast } = useToasts();

let lastFetchError = null;

const entries = ref([]);
const loading = ref(true);

async function loadEntries() {
  loading.value = true;
  try {
    entries.value = await fetchLog(activeSetupId.value);
    lastFetchError = null;
  } catch (error) {
    if (error.message !== lastFetchError) {
      lastFetchError = error.message;
      pushToast(`History: ${error.message}`);
    }
    entries.value = [];
  } finally {
    loading.value = false;
  }
}

// Deferred initial load (same reasoning as full_marks.vue/graph.vue — let
// the panel's own open animation paint first), plain re-fetch on setup
// switch since there's no heavy compute to chunk here, just one request.
useDeferredCompute(() => loadEntries());
watch(activeSetupId, () => loadEntries());

// Setup Description's Save button can log a 'description' entry while this
// panel is already open (see setup_log.js's onDescriptionLogged) — reload
// so it shows up without needing the panel to be closed and reopened.
const unsubDescriptionLogged = onDescriptionLogged((setupId) => {
  if (setupId === activeSetupId.value) loadEntries();
});
onBeforeUnmount(unsubDescriptionLogged);

const noteText = ref('');
const addingNote = ref(false);

async function onAddNote() {
  const text = noteText.value.trim();
  if (!text) return;

  addingNote.value = true;
  try {
    await addNote(activeSetupId.value, text);
    noteText.value = '';
    await loadEntries();
  } catch (error) {
    pushToast(`Failed to add note: ${error.message}`);
  } finally {
    addingNote.value = false;
  }
}

const now = useNowTicker();

function entryLabel(entry) {
  if (entry.kind === 'calc') return 'Calculated';
  if (entry.kind === 'description') return 'Description changed';
  return 'Note';
}

// Re-evaluate as time passes, not just when the underlying list changes —
// see formatTimeAgo callers elsewhere (e.g. input_sight_settings.vue).
function timeAgo(createdAt) {
  now.value;
  return formatTimeAgo(createdAt);
}

// ── Human-readable settings labels ──
// entry.data.settings carries raw SightSettings field names (see
// setup_log.js's onComputeFinished) — this is purely a display concern, so
// the mapping lives here rather than on the model itself.
const SETTINGS_LABELS = {
  eye_to_sight: { label: 'Eye to Sight', unit: 'mm' },
  arrow_to_eye: { label: 'Arrow to Eye', unit: 'mm' },
  vertical_tpi: { label: 'Sight TPI Vertical', unit: '' },
  horizontal_tpi: { label: 'Sight TPI Horizontal', unit: '' },
  sight_scale: { label: 'Sight Scale', unit: '' },
  clicks_per_turn: { label: 'Clicks per Turn', unit: '' },
  sight_bar_spacing: { label: 'Sight Bar Spacing', unit: 'mm' },
  sight_bar_position: { label: 'Sight Bar Position', unit: '' },
};

function settingLabel(key) {
  return SETTINGS_LABELS[key]?.label ?? key;
}

function settingValue(key, value) {
  const unit = SETTINGS_LABELS[key]?.unit;
  return unit ? `${value}${unit}` : `${value}`;
}

function markLabel(mark) {
  return `${mark.distance}${mark.unit}`;
}

// ── Diff against the previous calc entry ──
//
// `entries` is newest-first (see loadEntries/fetchLog), so for a given calc
// entry the previous one time-wise is simply the *next* calc entry later in
// the same array — skipping over any note/description entries in between,
// which don't carry settings/marks to diff against.
function diffSettings(current, previous) {
  const keys = new Set([...Object.keys(current || {}), ...Object.keys(previous || {})]);
  const changes = [];
  for (const key of keys) {
    if (current?.[key] !== previous?.[key]) {
      changes.push({ key, from: previous?.[key], to: current?.[key] });
    }
  }
  return changes;
}

function diffMarks(current, previous) {
  const prevByKey = new Map((previous || []).map((m) => [markLabel(m), m]));
  const curByKey = new Map((current || []).map((m) => [markLabel(m), m]));
  const changes = [];
  for (const [key, cur] of curByKey) {
    const prev = prevByKey.get(key);
    if (!prev) changes.push({ key, type: 'added', mark: cur.mark });
    else if (prev.mark !== cur.mark) changes.push({ key, type: 'changed', from: prev.mark, to: cur.mark });
  }
  for (const [key] of prevByKey) {
    if (!curByKey.has(key)) changes.push({ key, type: 'removed' });
  }
  return changes;
}

// Previous calc entry for each calc entry, keyed by id — recomputed
// whenever the list reloads (switching setups, adding/editing/deleting).
function previousCalcEntry(entry) {
  const calcEntries = entries.value.filter((e) => e.kind === 'calc');
  const index = calcEntries.findIndex((e) => e.id === entry.id);
  return index >= 0 ? calcEntries[index + 1] : undefined;
}

function calcDiff(entry) {
  const previous = previousCalcEntry(entry);
  if (!previous) return null;
  return {
    settings: diffSettings(entry.data.settings, previous.data.settings),
    marks: diffMarks(entry.data.marks, previous.data.marks),
  };
}

// ── Expand/collapse the full settings+marks tables ──
const expandedIds = ref(new Set());

function toggleExpand(id) {
  const next = new Set(expandedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedIds.value = next;
}

// ── Edit/delete a note ──
//
// Only 'note' entries — see setup_log.js's updateNote/deleteNote.
const editingId = ref(null);
const editText = ref('');
const editDate = ref('');
const savingEdit = ref(false);
const deletingId = ref(null);

function toDatetimeLocal(ts) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function startEdit(entry) {
  editingId.value = entry.id;
  editText.value = entry.data.text;
  editDate.value = toDatetimeLocal(entry.created_at);
}

function cancelEdit() {
  editingId.value = null;
}

async function saveEdit(entry) {
  const text = editText.value.trim();
  if (!text) return;
  const createdAt = new Date(editDate.value).getTime();
  if (!Number.isFinite(createdAt)) return;

  savingEdit.value = true;
  try {
    await updateNote(activeSetupId.value, entry.id, text, createdAt);
    editingId.value = null;
    await loadEntries();
  } catch (error) {
    pushToast(`Failed to save note: ${error.message}`);
  } finally {
    savingEdit.value = false;
  }
}

async function deleteEntry(entry) {
  deletingId.value = entry.id;
  try {
    await deleteNote(activeSetupId.value, entry.id);
    editingId.value = null;
    await loadEntries();
  } catch (error) {
    pushToast(`Failed to delete note: ${error.message}`);
  } finally {
    deletingId.value = null;
  }
}
</script>

<template>
  <div class="centered">
    <div class="note-input-row">
      <textarea
        class="note-input"
        placeholder="Add a note…"
        v-model="noteText"
        @keydown.enter.meta="onAddNote"
        @keydown.enter.ctrl="onAddNote"
      ></textarea>
      <button type="button" class="btn-add" @click="onAddNote" :disabled="addingNote || !noteText.trim()">
        {{ addingNote ? 'Adding…' : '+ Add Note' }}
      </button>
    </div>

    <p v-if="loading" class="status-line">Loading…</p>
    <p v-else-if="entries.length === 0" class="status-line">No history yet — it fills in as you compute, add notes, or edit the description.</p>

    <ul v-else class="entry-list">
      <li v-for="entry in entries" :key="entry.id" class="entry-row">
        <div class="entry-header">
          <span class="entry-label">{{ entryLabel(entry) }}</span>
          <span class="entry-time" :title="new Date(entry.created_at).toLocaleString()">
            {{ new Date(entry.created_at).toLocaleString() }} ({{ timeAgo(entry.created_at) }})
          </span>
        </div>

        <!-- ── calc entries: velocity + diff from last entry, expandable ── -->
        <template v-if="entry.kind === 'calc'">
          <p class="entry-velocity">{{ entry.data.velocity.toFixed(1) }} fps</p>

          <ul v-if="calcDiff(entry) && (calcDiff(entry).settings.length || calcDiff(entry).marks.length)" class="change-list">
            <li v-for="change in calcDiff(entry).settings" :key="'s-' + change.key">
              {{ settingLabel(change.key) }}: {{ settingValue(change.key, change.from) }} → {{ settingValue(change.key, change.to) }}
            </li>
            <li v-for="change in calcDiff(entry).marks" :key="'m-' + change.key">
              <template v-if="change.type === 'added'">Mark at {{ change.key }}: added ({{ change.mark }})</template>
              <template v-else-if="change.type === 'removed'">Mark at {{ change.key }}: removed</template>
              <template v-else>Mark at {{ change.key }}: {{ change.from }} → {{ change.to }}</template>
            </li>
          </ul>

          <button type="button" class="btn-expand" @click="toggleExpand(entry.id)">
            {{ expandedIds.has(entry.id) ? 'Hide details ▼' : 'Show details ▶' }}
          </button>

          <div v-if="expandedIds.has(entry.id)" class="details-tables">
            <div class="table-scroll">
              <table class="entry-table">
                <tbody>
                  <tr v-for="(value, key) in entry.data.settings" :key="key">
                    <th>{{ settingLabel(key) }}</th>
                    <td>{{ settingValue(key, value) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="entry.data.marks && entry.data.marks.length" class="table-scroll">
              <table class="entry-table">
                <thead>
                  <tr>
                    <th>Distance</th>
                    <th>Mark</th>
                    <th>Sight Bar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(mark, index) in entry.data.marks" :key="index">
                    <td>{{ markLabel(mark) }}</td>
                    <td>{{ mark.mark }}</td>
                    <td>{{ mark.sight_bar_position }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <!-- ── note entries: editable/deletable ── -->
        <template v-else-if="entry.kind === 'note'">
          <template v-if="editingId === entry.id">
            <textarea class="note-input" v-model="editText"></textarea>
            <input type="datetime-local" class="input-measure edit-date" v-model="editDate" />
            <div class="edit-actions">
              <button type="button" class="btn-copy" @click="saveEdit(entry)" :disabled="savingEdit || !editText.trim()">
                {{ savingEdit ? 'Saving…' : 'Save' }}
              </button>
              <button type="button" class="btn-copy" @click="cancelEdit" :disabled="savingEdit">Cancel</button>
              <button type="button" class="btn-delete-note" @click="deleteEntry(entry)" :disabled="deletingId === entry.id">
                {{ deletingId === entry.id ? 'Deleting…' : 'Delete' }}
              </button>
            </div>
          </template>
          <template v-else>
            <p class="entry-detail">{{ entry.data.text }}</p>
            <button type="button" class="btn-expand" @click="startEdit(entry)">Edit</button>
          </template>
        </template>

        <!-- ── description entries: plain text, not editable ── -->
        <p v-else class="entry-detail">{{ entry.data.text }}</p>
      </li>
    </ul>
  </div>
</template>

<style scoped>

.note-input-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  width: 100%;
  max-width: 40em;
}

.note-input {
  width: 100%;
  min-height: 3em;
  padding: var(--space-sm);
  font: inherit;
  font-size: var(--font-size-sm);
  color: var(--color-text);
  background: var(--color-bg);
  border: var(--border);
  border-radius: var(--border-radius);
  resize: vertical;
  box-sizing: border-box;
}

.status-line {
  opacity: 0.7;
  font-size: var(--font-size-sm);
}

.entry-list {
  list-style: none;
  margin: var(--space-md) 0 0;
  padding: 0;
  width: 100%;
  max-width: 60em;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.entry-row {
  padding: var(--space-sm);
  border: var(--border);
  border-radius: var(--border-radius);
  background: var(--color-bg-alt);
  text-align: center;
}

.entry-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--space-sm);
  flex-wrap: wrap;
}

.entry-label {
  font-weight: var(--font-weight-semibold);
}

.entry-time {
  font-size: var(--font-size-sm);
  opacity: 0.6;
  white-space: nowrap;
}

.entry-detail {
  margin: var(--space-xs) 0 0;
  font-size: var(--font-size-sm);
  word-break: break-word;
}

.entry-velocity {
  margin: var(--space-xs) 0 var(--space-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.change-list {
  margin: 0 0 var(--space-sm);
  padding: 0;
  list-style: none;
  font-size: var(--font-size-sm);
}

.btn-expand {
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--font-size-sm);
  background: none;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--border-radius);
  color: var(--color-text);
  cursor: pointer;
}

.btn-expand:hover {
  background: var(--color-bg);
}

.details-tables {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-lg);
  margin-top: var(--space-sm);
}

.table-scroll {
  overflow-x: auto;
}

.entry-table {
  border-collapse: collapse;
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.entry-table th,
.entry-table td {
  padding: 2px var(--space-sm);
  text-align: center;
  font-weight: normal;
}

.entry-table th {
  color: var(--color-text);
  opacity: 0.6;
}

.edit-date {
  margin-top: var(--space-xs);
  width: auto;
}

.edit-actions {
  display: flex;
  gap: var(--space-xs);
  margin-top: var(--space-sm);
}

.btn-delete-note {
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--font-size-sm);
  background: var(--color-danger);
  color: var(--color-text-light);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  margin-left: auto;
}

.btn-delete-note:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

</style>
